import { useState } from "react";
import { sendFeedback } from "@unfaithful/client";

/**
 * Test-night feedback from the phones: a rating when the game ends, and a
 * "Report a problem" that's there the whole game. Both say which castle, phase
 * and player they came from, so the admin can line them up with what happened.
 */

const sentKey = (castle: string) => `playpop.rated.${castle}`;

function alreadyRated(castle: string): boolean {
  try {
    return sessionStorage.getItem(sentKey(castle)) === "1";
  } catch {
    return false;
  }
}

const STAR_WORDS = ["", "Didn't enjoy it", "Not great", "It was fine", "Good fun", "Loved it"];

/** "How was it?" at the finale. */
export function RateGame({ castle }: { castle: string }) {
  const [stars, setStars] = useState(0);
  const [note, setNote] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">(() => (alreadyRated(castle) ? "sent" : "idle"));

  if (state === "sent") return <p className="quiet rate-thanks">Thanks. Your rating was sent.</p>;

  const send = async () => {
    setState("sending");
    const ok = await sendFeedback({ kind: "rating", app: "phone", rating: stars, message: note.trim() });
    if (ok) {
      try {
        sessionStorage.setItem(sentKey(castle), "1");
      } catch {
        /* private mode */
      }
    }
    setState(ok ? "sent" : "failed");
  };

  return (
    <section className="rate-game" aria-label="Rate this game">
      <p className="rate-title">How was it?</p>
      <div className="stars" role="radiogroup" aria-label="Stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            role="radio"
            aria-checked={stars === n}
            aria-label={`${n} star${n > 1 ? "s" : ""}: ${STAR_WORDS[n]}`}
            className={`star${n <= stars ? " is-on" : ""}`}
            onClick={() => setStars(n)}
          >
            ★
          </button>
        ))}
      </div>
      <p className="quiet star-word">{stars ? STAR_WORDS[stars] : "Tap a star"}</p>
      {stars > 0 && (
        <>
          <textarea
            className="text-input feedback-note"
            rows={3}
            maxLength={2000}
            placeholder="Anything that confused you, felt slow or broke? (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button className="primary wax-button" disabled={state === "sending"} onClick={() => void send()}>
            {state === "sending" ? "Sending…" : "Send rating"}
          </button>
          {state === "failed" && <p className="error">That didn't send. Check your signal and try again.</p>}
        </>
      )}
    </section>
  );
}

const QUICK = ["Stuck or frozen", "Lost connection", "No sound", "Didn't know what to do", "Something looked wrong"];

/** A small link that opens into a short form, inline in the page. */
export function ReportProblem({ app = "phone" }: { app?: string }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");

  if (!open) {
    return (
      <button
        className="text-button report-link"
        onClick={() => {
          setOpen(true);
          setState("idle");
        }}
      >
        Report a problem
      </button>
    );
  }

  if (state === "sent") {
    return (
      <section className="report-box" aria-live="polite">
        <p className="quiet">Sent. Thanks, that helps.</p>
        <button
          className="text-button"
          onClick={() => {
            setOpen(false);
            setPicked([]);
            setNote("");
          }}
        >
          Done
        </button>
      </section>
    );
  }

  const toggle = (q: string) => setPicked((p) => (p.includes(q) ? p.filter((x) => x !== q) : [...p, q]));
  const canSend = picked.length > 0 || note.trim().length > 0;
  const send = async () => {
    setState("sending");
    const message = [picked.join(", "), note.trim()].filter(Boolean).join(": ");
    const ok = await sendFeedback({ kind: "problem", app, message });
    setState(ok ? "sent" : "failed");
  };

  return (
    <section className="report-box" aria-label="Report a problem">
      <p className="report-title">What went wrong?</p>
      <div className="chip-row">
        {QUICK.map((q) => (
          <button key={q} className={`chip${picked.includes(q) ? " is-on" : ""}`} aria-pressed={picked.includes(q)} onClick={() => toggle(q)}>
            {q}
          </button>
        ))}
      </div>
      <textarea
        className="text-input feedback-note"
        rows={3}
        maxLength={2000}
        placeholder="What happened? (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="report-actions">
        <button className="secondary moon-button" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button className="primary wax-button" disabled={!canSend || state === "sending"} onClick={() => void send()}>
          {state === "sending" ? "Sending…" : "Send"}
        </button>
      </div>
      {state === "failed" && <p className="error">That didn't send. Check your signal and try again.</p>}
    </section>
  );
}
